/**
 * Offline API Wrapper
 * Intercepts API calls and queues them when offline
 */

import { offlineQueue, QueuedOperation } from './offlineQueue';
import { useAppStore } from '@/stores/appStore';

/**
 * Make an API call with offline support
 * If online: makes the call normally
 * If offline: queues the operation and returns optimistic response
 */
export async function offlineAwareApiCall<T = any>(
  endpoint: string,
  options: RequestInit & {
    sessionId?: string;
    userId?: string;
    operationType?: QueuedOperation['type'];
    optimisticData?: any;
  }
): Promise<{ success: boolean; data?: T; queued?: boolean; error?: string }> {
  const { sessionId, userId, operationType, optimisticData, ...fetchOptions } = options;

  // Check if we're offline
  const isOnline = navigator.onLine;
  
  if (!isOnline) {
    // Queue the operation
    const opType = operationType || inferOperationType(endpoint, fetchOptions.method as string);
    
    const operationId = offlineQueue.enqueue({
      type: opType,
      endpoint,
      method: (fetchOptions.method as 'POST' | 'PUT' | 'DELETE' | 'PATCH') || 'POST',
      body: fetchOptions.body ? JSON.parse(fetchOptions.body as string) : null,
      sessionId: sessionId || '',
      userId: userId || '',
    });

    // Update pending count in store
    const pendingCount = offlineQueue.getPendingOperations().length;
    useAppStore.getState().setPendingOperationsCount(pendingCount);

    // Return optimistic response
    return {
      success: true,
      data: optimisticData || { id: operationId, queued: true },
      queued: true,
    };
  }

  // Online - make the actual API call
  try {
    const response = await fetch(endpoint, fetchOptions);
    
    if (!response.ok) {
      const errorText = await response.text();
      return {
        success: false,
        error: errorText || `HTTP ${response.status}`,
      };
    }

    const result = await response.json();
    
    // If API returns { success, data }, unwrap it
    // Otherwise return the result directly
    if (result.success !== undefined && result.data !== undefined) {
      return {
        success: result.success,
        data: result.data,
      };
    }
    
    return {
      success: true,
      data: result,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
    };
  }
}

/**
 * Sync all pending operations
 */
export async function syncPendingOperations(
  onProgress?: (current: number, total: number, operation: QueuedOperation) => void
): Promise<{ success: number; failed: number; total: number }> {
  const pendingOps = offlineQueue.getPendingOperations();
  
  if (pendingOps.length === 0) {
    return { success: 0, failed: 0, total: 0 };
  }

  let successCount = 0;
  let failedCount = 0;

  for (let i = 0; i < pendingOps.length; i++) {
    const op = pendingOps[i];
    
    if (onProgress) {
      onProgress(i + 1, pendingOps.length, op);
    }

    // Mark as syncing
    offlineQueue.updateStatus(op.id, 'syncing');

    try {
      // Make the actual API call
      const response = await fetch(op.endpoint, {
        method: op.method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(op.body),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${await response.text()}`);
      }

      const result = await response.json();

      if (result.success !== false) {
        // Success - mark as completed and remove from queue
        offlineQueue.updateStatus(op.id, 'success');
        successCount++;
        
        // Remove successful operations after a brief delay
        setTimeout(() => offlineQueue.remove(op.id), 1000);
      } else {
        throw new Error(result.error || 'API returned success: false');
      }

    } catch (error) {
      // Try to retry
      const canRetry = offlineQueue.incrementRetry(op.id);
      
      if (canRetry) {
        // Mark as pending for retry
        offlineQueue.updateStatus(op.id, 'pending', error instanceof Error ? error.message : 'Unknown error');
      } else {
        // Max retries exceeded
        offlineQueue.updateStatus(op.id, 'failed', `Max retries exceeded: ${error instanceof Error ? error.message : 'Unknown error'}`);
        failedCount++;
      }
    }
  }

  // Update pending count in store
  const remainingPending = offlineQueue.getPendingOperations().length;
  useAppStore.getState().setPendingOperationsCount(remainingPending);

  return {
    success: successCount,
    failed: failedCount,
    total: pendingOps.length,
  };
}

/**
 * Infer operation type from endpoint and method
 */
function inferOperationType(endpoint: string, method: string): QueuedOperation['type'] {
  if (endpoint.includes('/annotations')) {
    return method === 'DELETE' ? 'delete' : 'annotation';
  }
  if (endpoint.includes('/signatures')) {
    return method === 'DELETE' ? 'delete' : 'signature';
  }
  if (endpoint.includes('/user-initials')) {
    return 'initial';
  }
  if (endpoint.includes('/signature-fields')) {
    return 'field';
  }
  if (endpoint.includes('/comment')) {
    return 'comment';
  }
  return 'annotation'; // Default
}

/**
 * Check if we should warn about storage
 */
export function checkStorageWarning(): { shouldWarn: boolean; stats: any } {
  const stats = offlineQueue.getStorageStats();
  return {
    shouldWarn: stats.nearQuota,
    stats,
  };
}

/**
 * Get pending operations count
 */
export function getPendingCount(): number {
  return offlineQueue.getPendingOperations().length;
}

/**
 * Clear all pending operations (use with caution)
 */
export function clearPendingOperations(): void {
  offlineQueue.clear();
  useAppStore.getState().setPendingOperationsCount(0);
}
